import * as Sentry from "@sentry/react";
import { debugLog } from '../utils/logger';
import { CreationStrategy } from '../types/creation';
import { 
  AuthenticationResponse, 
  TelegramAuthRequest
} from '../types';

interface TelegramUserData {
  id: number;
  username: string;
  first_name: string;
  last_name?: string | null;
  photo_url?: string;
}

// Legacy interface for backward compatibility
interface Token {
  access_token: string;
  token_type: string;
}

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface UserConfig {
  onboarding_completed: boolean;
  onboarding_completed_at: string | null;
  telegram_file_type: 'document' | 'video';
}

export enum PaymentType {
  SYSTEM_ACCESS = "SYSTEM_ACCESS",
  OTHER = "OTHER"
}

export enum PaymentStatus {
  PENDING = "PENDING",
  VALIDATING = "VALIDATING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED"
}

interface PaymentStatusResponse {
  status: PaymentStatus;
  error_message?: string;
}

interface InvoiceResponse {
  invoice_url: string;
  payment_id: string;
}

interface Invitation {
  id: string;
  expires_at: string;
}

export interface CreationListItem {
  id: string;
  created_at: string;
  updated_at: string;
  status: CreationStatus;
  uploaded_files: string[];
  result_files: string[];
  payment_id: string | null;
  pipelines: string[];
  strategy: {
    name: string;
  };
}

interface PaginatedCreationsResponse {
  items: CreationListItem[];
  total: number;
  page: number;
  per_page: number;
}

export interface CreationResponse {
  id: string;
  created_at: string;
  updated_at: string;
  status: CreationStatus;
  uploaded_files: FileResponse[];
  result_files: FileResponse[];
  payment_id: string | null;
  pipelines: string[];
  strategy: CreationStrategy;
  name?: string;
  type?: string;
  metadata?: {
    best_moments?: Array<{
      viral_score: number;
      hook_rating: string;
      flow_rating: string;
      value_rating: string;
      shareability_rating: string;
      hook_explanation: string;
      flow_explanation: string;
      value_explanation: string;
      shareability_explanation: string;
      trend_alignment: string;
      audience_engagement_potential: string;
      quality_score: number;
      quality_explanation: string;
      text: string;
      start_time: number;
      end_time: number;
      duration: number;
      reason: string;
      chunk_id: number;
      file_name: string;
      original_words: string[];
    }>;
    [key: string]: unknown;
  };
  is_shared?: boolean;
  has_access?: boolean;
  owner?: {
    id: string;
    username?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  };
}

export enum CreationStatus {
  DRAFT = "draft",
  PENDING = "pending",
  WAITING_FOR_PAYMENT = "waiting_for_payment",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed"
}

export interface FileResponse {
  id: string;
  name: string;
  url: string;
  mime_type?: string;
  thumbnail_url?: string;
}

interface PartInfo {
  PartNumber: number;
  ETag: string;
}


export interface MultipartUploadResponse {
  upload_id: string;
  file_id: string;
}

// Minimum chunk size for multipart uploads
const MIN_CHUNK_SIZE = 15 * 1024 * 1024;
// Try to keep number of parts reasonable in case the backend has limits
const MAX_PARTS = 5000;
// Number of retries for each uploaded part
const PART_RETRY_COUNT = 3;

export interface UploadProgressCallback {
  (progress: number): void;
}

interface CreatePaymentRequest {
  title: string;
  description: string;
  currency: string;
  amount: number;
  payment_type: 'SYSTEM_ACCESS' | 'BALANCE_TOPUP';
}

interface CreatePaymentResponse {
  payment_id: string;
  invoice_url: string;
}

// Define types for User and PaginatedUsersResponse based on the OpenAPI spec
// These types were previously in AdminPanel.tsx, moved here for broader use
export interface ShortUserResponse {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  photo_url?: string | null;
}

export interface PaginatedUsersResponse {
  total: number;
  page: number;
  per_page: number;
  users: ShortUserResponse[];
}

export class ApiClient {
  private static baseUrl = import.meta.env.VITE_API_URL;
  private static impersonatedUserId: string | null = null;
  private static useOriginalUserOverride: boolean = false;

  public static DANGEROUSLY_makeNextRequestAsOriginalUser(): void {
    this.useOriginalUserOverride = true;
  }

  private static getStoredToken(): string | null {
    return localStorage.getItem('token');
  }

  private static storeTelegramAuth(initData: string, userData: TelegramUserData): void {
    localStorage.setItem('tg_init_data', initData);
    localStorage.setItem('tg_user_data', JSON.stringify(userData));
  }

  private static loadTelegramAuth(): { initData: string; userData: TelegramUserData } | null {
    const initData = localStorage.getItem('tg_init_data');
    const userDataStr = localStorage.getItem('tg_user_data');
    if (!initData || !userDataStr) {
      return null;
    }
    try {
      return { initData, userData: JSON.parse(userDataStr) as TelegramUserData };
    } catch {
      return null;
    }
  }

  private static getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.getStoredToken() ? { 'Authorization': `Bearer ${this.getStoredToken()}` } : {}),
    };
    
    const forceOriginal = this.useOriginalUserOverride;
    if (forceOriginal) {
      this.useOriginalUserOverride = false;
    }

    if (!forceOriginal && this.impersonatedUserId) {
      headers['X-Impersonate-User-Id'] = this.impersonatedUserId;
    }
    return headers;
  }
  
  private static pendingRequests = new Map<string, Promise<unknown>>();
  private static pendingAuthRequests = new Map<string, Promise<AuthenticationResponse | Token>>();

  private static async fetchWithAuth(
    endpoint: string,
    options: RequestInit = {},
    retry = true
  ): Promise<Response> {
    const authHeaders = this.getAuthHeaders();
    
    // Don't set Content-Type for FormData - let browser set multipart boundary
    if (options.body instanceof FormData) {
      delete (authHeaders as Record<string, string>)['Content-Type'];
    }
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (response.status !== 401 || !retry) {
      return response;
    }

    const tg = this.loadTelegramAuth();
    if (!tg) {
      return response;
    }

    try {
      const authResult = await this.authenticateTelegram(tg.initData, tg.userData);
      localStorage.setItem('token', authResult.access_token);
    } catch {
      return response;
    }

    return this.fetchWithAuth(endpoint, options, false);
  }

  // Telegram authentication using new backend endpoints
  static async authenticateTelegram(initData: string, userData: TelegramUserData): Promise<AuthenticationResponse> {
    const cacheKey = `AUTH-TELEGRAM-${userData.id}`;
    
    const existingRequest = this.pendingAuthRequests.get(cacheKey);
    if (existingRequest) {
      debugLog(`Using cached Telegram authentication request for user ${userData.id}`);
      return existingRequest as Promise<AuthenticationResponse>;
    }
    
    const requestPromise = (async () => {
      debugLog(`Making Telegram authentication request for user ${userData.id}`);
      
      const response = await fetch(`${this.baseUrl}/api/auth/telegram/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          init_data: initData,
          user_data: userData,
        } as TelegramAuthRequest),
      });

      if (response.status === 404) {
        // User doesn't exist, try to register
        // Remove this request from cache before making a new one
        this.pendingAuthRequests.delete(cacheKey);
        return this.registerTelegram(initData, userData);
      }

      if (!response.ok) {
        const errorMessage = `Telegram authentication failed: ${response.statusText}`;
        Sentry.captureMessage(errorMessage, {
          level: "error",
          tags: {
            method: "authenticateTelegram",
            status: response.status.toString()
          },
          extra: {
            userId: userData.id
          }
        });
        throw new Error(errorMessage);
      }

      const data: AuthenticationResponse = await response.json();
      this.storeTelegramAuth(initData, userData);
      return data;
    })();
    
    // Store the request in the cache
    this.pendingAuthRequests.set(cacheKey, requestPromise);
    
    try {
      // Execute the request
      const result = await requestPromise;
      return result;
    } finally {
      // Clear the cache when done (success or error)
      setTimeout(() => {
        this.pendingAuthRequests.delete(cacheKey);
      }, 0);
    }
  }

  static async registerTelegram(initData: string, userData: TelegramUserData): Promise<AuthenticationResponse> {
    const cacheKey = `REGISTER-TELEGRAM-${userData.id}`;
    
    const existingRequest = this.pendingAuthRequests.get(cacheKey);
    if (existingRequest) {
      debugLog(`Using cached Telegram registration request for user ${userData.id}`);
      return existingRequest as Promise<AuthenticationResponse>;
    }
    
    const requestPromise = (async () => {
      debugLog(`Making Telegram registration request for user ${userData.id}`);
      
      const response = await fetch(`${this.baseUrl}/api/auth/telegram/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          init_data: initData,
          user_data: userData,
        } as TelegramAuthRequest),
      });
      
      if (!response.ok) {
        const errorMessage = `Telegram registration failed: ${response.statusText}`;
        Sentry.captureMessage(errorMessage, {
          level: "error",
          tags: {
            method: "registerTelegram",
            status: response.status.toString()
          },
          extra: {
            userId: userData.id
          }
        });
        throw new Error(errorMessage);
      }

      const data: AuthenticationResponse = await response.json();
      this.storeTelegramAuth(initData, userData);
      return data;
    })();
    
    // Store the request in the cache
    this.pendingAuthRequests.set(cacheKey, requestPromise);
    
    try {
      // Execute the request
      const result = await requestPromise;
      return result;
    } finally {
      // Clear the cache when done (success or error)
      setTimeout(() => {
        this.pendingAuthRequests.delete(cacheKey);
      }, 0);
    }
  }



  // Legacy method for backward compatibility
  static async authenticate(initData: string, userData: TelegramUserData): Promise<Token> {
    const result = await this.authenticateTelegram(initData, userData);
    return {
      access_token: result.access_token,
      token_type: result.token_type
    };
  }

  // Legacy method for backward compatibility
  static async register(initData: string, userData: TelegramUserData): Promise<Token> {
    const result = await this.registerTelegram(initData, userData);
    return {
      access_token: result.access_token,
      token_type: result.token_type
    };
  }

  static async makeAuthenticatedRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    // Cache key no longer needs forceOriginalUser explicitly as it's handled by override state
    const cacheKey = `${options.method || 'GET'}-${endpoint}-${JSON.stringify(options.body || {})}`;
    
    // Check if this request is already in progress
    const existingRequest = this.pendingRequests.get(cacheKey);
    if (existingRequest) {
      debugLog(`Using cached request for ${cacheKey}`);
      return existingRequest as Promise<T>;
    }
    
    // Create the new request
    const requestPromise = (async () => {
      debugLog(`Making API request: ${options.method || 'GET'} ${endpoint}`);
      
      const response = await this.fetchWithAuth(endpoint, options);
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `Request failed: ${response.statusText}`);
      }
  
      const data = await response.json();
      debugLog(`API response for ${endpoint}: success`);
      return data;
    })();
    
    // Store the request in the cache
    this.pendingRequests.set(cacheKey, requestPromise);
    
    try {
      // Execute the request
      const result = await requestPromise;
      return result;
    } finally {
      // Clear the cache when done (success or error)
      setTimeout(() => {
        this.pendingRequests.delete(cacheKey);
      }, 0);
    }
  }

  static async getUserInfo(): Promise<UserInfo> {
    return this.makeAuthenticatedRequest<UserInfo>('/api/users/me');
  }

  static async getAllUsers(page: number = 1, perPage: number = 20): Promise<PaginatedUsersResponse> {
    this.DANGEROUSLY_makeNextRequestAsOriginalUser(); // Set override before the call
    return this.makeAuthenticatedRequest<PaginatedUsersResponse>(
      `/api/users/all?page=${page}&per_page=${perPage}`
    );
  }

  static async getUserConfig(): Promise<UserConfig> {
    return this.makeAuthenticatedRequest<UserConfig>('/api/users/me/config');
  }

  static async updateTelegramFileType(telegramFileType: 'document' | 'video'): Promise<{ message: string; telegram_file_type: string }> {
    return this.makeAuthenticatedRequest<{ message: string; telegram_file_type: string }>('/api/users/me/config/telegram-file-type', {
      method: 'POST',
      body: JSON.stringify({ telegram_file_type: telegramFileType })
    });
  }

  static async completeOnboarding(): Promise<void> {
    return this.makeAuthenticatedRequest<void>('/api/users/me/onboarding/complete', {
      method: 'POST'
    });
  }

  static async purchaseSystemAccess(): Promise<InvoiceResponse> {
    return this.makeAuthenticatedRequest<InvoiceResponse>('/api/payments/system-access', {
      method: 'POST'
    });
  }

  static async getPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    return this.makeAuthenticatedRequest<PaymentStatusResponse>(`/api/payments/${paymentId}`);
  }

  static async generateInvitation(): Promise<Invitation> {
    return this.makeAuthenticatedRequest<Invitation>('/api/invitations', {
      method: 'POST'
    });
  }

  static async acceptInvitation(invitationId: string): Promise<void> {
    return this.makeAuthenticatedRequest<void>(`/api/invitations/${invitationId}/accept`, {
      method: 'POST'
    });
  }

  static async getCreations(page: number = 1, perPage: number = 10, query?: string): Promise<PaginatedCreationsResponse> {
    const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
    if (query) params.append('query', query);
    return this.makeAuthenticatedRequest<PaginatedCreationsResponse>(`/api/creations?${params.toString()}`);
  }

  static async getCreation(id: string): Promise<CreationResponse> {
    return this.makeAuthenticatedRequest<CreationResponse>(`/api/creations/${id}`);
  }

  static async deleteCreation(id: string): Promise<void> {
    return this.makeAuthenticatedRequest<void>(`/api/creations/${id}`, {
      method: 'DELETE'
    });
  }

  static async retriggerCreation(id: string): Promise<CreationResponse> {
    return this.makeAuthenticatedRequest<CreationResponse>(`/api/creations/${id}/retrigger`, {
      method: 'POST'
    });
  }

  static async uploadLargeFile(file: File, onProgress?: UploadProgressCallback): Promise<FileResponse> {
    let uploadId: string | undefined;
    let fileId: string | undefined;

    // Choose chunk size dynamically to avoid too many parts
    const chunkSize = Math.max(MIN_CHUNK_SIZE, Math.ceil(file.size / MAX_PARTS));

    try {
      // Start multipart upload
      const formData = new FormData();
      formData.append('filename', file.name);
      
      debugLog(`Starting multipart upload for file: ${file.name}, size: ${file.size} bytes`);
      const startResponse = await this.fetchWithAuth('/api/files/upload/start', {
        method: 'POST',
        body: formData,
      });

      if (!startResponse.ok) {
        throw new Error('Failed to start multipart upload');
      }

      const { upload_id, file_id } = await startResponse.json() as MultipartUploadResponse;
      uploadId = upload_id;
      fileId = file_id;
      debugLog(`Upload started - upload_id: ${upload_id}, file_id: ${file_id}`);

      // Upload parts
      const totalChunks = Math.ceil(file.size / chunkSize);
      const partInfo: PartInfo[] = [];
      debugLog(`Will upload ${totalChunks} chunks of size ${chunkSize} bytes each`);

      for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        debugLog(`Uploading part ${partNumber}/${totalChunks}, chunk size: ${chunk.size} bytes`);

        const partFormData = new FormData();
        partFormData.append('file', new Blob([chunk], { type: 'application/octet-stream' }), file.name);
        partFormData.append('upload_id', upload_id);
        partFormData.append('file_id', file_id);
        partFormData.append('part_number', partNumber.toString());
        partFormData.append('filename', file.name);

        let attempt = 0;
        let partResponse: Response | undefined;
        while (attempt < PART_RETRY_COUNT) {
          partResponse = await this.fetchWithAuth('/api/files/upload/part', {
            method: 'POST',
            body: partFormData,
          });
          if (partResponse.ok) {
            break;
          }
          attempt++;
          debugLog(`Part ${partNumber} upload attempt ${attempt} failed, retrying...`);
        }
        if (!partResponse || !partResponse.ok) {
          throw new Error(`Failed to upload part ${partNumber}`);
        }

        // Extract ETag from response body (not headers) and add to partInfo array
        const partResponseData = await partResponse.json();
        const etag = partResponseData.etag;
        if (!etag) {
          throw new Error(`No ETag received for part ${partNumber}`);
        }

        const cleanETag = etag.replace(/"/g, ''); // Remove quotes from ETag if present
        partInfo.push({
          PartNumber: partNumber,
          ETag: cleanETag
        });

        debugLog(`Part ${partNumber} uploaded successfully, ETag: ${cleanETag}`);

        if (onProgress) {
          onProgress((partNumber / totalChunks) * 90); // Reserve 10% for completion
        }
      }

      debugLog(`All parts uploaded successfully. Parts array:`, partInfo);

      // Complete multipart upload
      debugLog(`Completing multipart upload with ${partInfo.length} parts`);
      const completeResponse = await this.fetchWithAuth('/api/files/upload/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          upload_id,
          file_id,
          filename: file.name,
          parts: partInfo
        }),
      });

      if (!completeResponse.ok) {
        const errorText = await completeResponse.text().catch(() => 'Unknown error');
        debugLog(`Complete upload failed with status ${completeResponse.status}: ${errorText}`);
        throw new Error(`Failed to complete multipart upload: ${completeResponse.status} - ${errorText}`);
      }

      debugLog(`Multipart upload completed successfully`);

      if (onProgress) {
        onProgress(100);
      }

      return completeResponse.json();
    } catch (error) {
      debugLog(`Multipart upload failed, aborting...`, error);
      if (uploadId && fileId) {
        await this.abortMultipartUpload(uploadId, fileId, file.name).catch(() => {});
      }
      throw error;
    }
  }

  static async abortMultipartUpload(uploadId: string, fileId: string, filename: string): Promise<void> {
    await this.fetchWithAuth(
      '/api/files/upload/abort?' +
        new URLSearchParams({
          upload_id: uploadId,
          file_id: fileId,
          filename: filename,
        }),
      {
        method: 'POST',
      }
    );
  }

  static async uploadFile(file: File, onProgress?: UploadProgressCallback): Promise<FileResponse> {
    // If file is larger than 32MB, use multipart upload
    if (file.size > 32 * 1024 * 1024) {
      return this.uploadLargeFile(file, onProgress);
    }

    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve(JSON.parse(xhr.response));
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', `${this.baseUrl}/api/files/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.getStoredToken()}`);
      xhr.send(formData);
    });
  }

  static async createKovrification(): Promise<CreationResponse> {
    return this.makeAuthenticatedRequest<CreationResponse>('/api/creations', {
      method: 'POST',
      body: JSON.stringify({
        strategy_type: 'kovrification'
      })
    });
  }

  static async createBestMoments(): Promise<CreationResponse> {
    return this.makeAuthenticatedRequest<CreationResponse>('/api/creations', {
      method: 'POST',
      body: JSON.stringify({
        strategy_type: 'podcast_best_moments'
      })
    });
  }

  static async configureKovrification(
    creationId: string,
    config: {
      video_processing: {
        vertical_video_proportion: number;
      },
      caption_config?: {
        style_id?: string;
        color?: string;
        accent_color?: string;
        font_size?: number;
        accent_font_size?: number;
        words_per_line?: number;
        vertical_margin?: number;
      },
      captions_enabled?: boolean;
    }
  ): Promise<void> {
    return this.makeAuthenticatedRequest<void>(`/api/creations/${creationId}/configure/kovrification`, {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  static async configureBestMoments(
    creationId: string,
    config: {
      captions_enabled: boolean;
      caption_config?: {
        style_id?: string;
        color?: string;
        accent_color?: string;
        font_size?: number;
        accent_font_size?: number;
        words_per_line?: number;
        vertical_margin?: number;
      };
    }
  ): Promise<void> {
    return this.makeAuthenticatedRequest<void>(`/api/creations/${creationId}/configure/podcast-best-moments`, {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  static async attachFiles(creationId: string, fileIds: string[]): Promise<void> {
    return this.makeAuthenticatedRequest<void>(`/api/creations/${creationId}/files`, {
      method: 'POST',
      body: JSON.stringify({
        files: fileIds
      })
    });
  }

  static async startProcessing(creationId: string): Promise<void> {
    return this.makeAuthenticatedRequest<void>(`/api/creations/${creationId}/process`, {
      method: 'POST'
    });
  }

  static async getCreationPrice(creationId: string): Promise<{ price: number }> {
    return this.makeAuthenticatedRequest<{ price: number }>(`/api/creations/${creationId}/price`);
  }

  static async createPayment(request: CreatePaymentRequest): Promise<CreatePaymentResponse> {
    const response = await this.fetchWithAuth('/api/payments/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error('Failed to create payment');
    }

    return response.json();
  }

  // Add a method to send a creation result file to the user's Telegram chat
  static async sendCreationToTelegram(
    creationId: string,
    request: {
      result_file_id: string;
      description?: string;
      button_text?: string;
      button_url?: string;
      send_as_document?: boolean; // Optional - when null/undefined, uses user's preference
    }
  ): Promise<Record<string, string>> {
    return this.makeAuthenticatedRequest<Record<string, string>>(
      `/api/creations/${creationId}/send-to-telegram`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
  }

  // Make a creation publicly available
  static async makeCreationPublic(creationId: string): Promise<CreationResponse> {
    return this.makeAuthenticatedRequest<CreationResponse>(
      `/api/creations/${creationId}/make-public`,
      {
        method: 'POST'
      }
    );
  }

  // Make a creation private (only accessible to its owner)
  static async makeCreationPrivate(creationId: string): Promise<CreationResponse> {
    return this.makeAuthenticatedRequest<CreationResponse>(
      `/api/creations/${creationId}/make-private`,
      {
        method: 'POST'
      }
    );
  }

  // Request access to a private creation
  static async requestCreationAccess(
    creationId: string, 
    request: {
      message?: string;
      button_text?: string;
      button_url?: string;
      parse_mode?: 'HTML' | 'MarkdownV2';
    }
  ): Promise<Record<string, string>> {
    return this.makeAuthenticatedRequest<Record<string, string>>(
      `/api/creations/${creationId}/request-access`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
  }

  static async updateSharing(creationId: string, isShared: boolean): Promise<CreationResponse> {
    return isShared 
      ? this.makeCreationPublic(creationId)
      : this.makeCreationPrivate(creationId);
  }

  static async requestAccess(id: string): Promise<Record<string, string>> {
    return this.requestCreationAccess(id, {
      message: "A user has requested access to your creation.",
      button_text: "View Creation",
    });
  }

  // Update a best moment's starred or hidden status
  static async updateBestMoment(
    creationId: string,
    momentIndex: number,
    update: {
      is_starred?: boolean;
      is_hidden?: boolean;
    }
  ): Promise<Record<string, unknown>> {
    return this.makeAuthenticatedRequest(
      `/api/creations/${creationId}/best_moments/${momentIndex}`,
      {
        method: 'PATCH',
        body: JSON.stringify(update)
      }
    );
  }

  // Get all best moments for a creation
  static async getBestMoments(creationId: string): Promise<Record<string, unknown>> {
    return this.makeAuthenticatedRequest(
      `/api/creations/${creationId}/best_moments`,
      {
        method: 'GET'
      }
    );
  }

  // Method to set/clear impersonation
  public static setImpersonatedUserId(userId: string | null): void {
    this.impersonatedUserId = userId;
    // Optionally, persist this to sessionStorage if needed across page reloads but not full sessions
    if (userId) {
      sessionStorage.setItem('impersonatedUserId', userId);
    } else {
      sessionStorage.removeItem('impersonatedUserId');
    }
  }

  // Initialize impersonatedUserId from sessionStorage on load
  public static initializeImpersonation(): void {
    const storedImpersonatedId = sessionStorage.getItem('impersonatedUserId');
    if (storedImpersonatedId) {
      this.impersonatedUserId = storedImpersonatedId;
    }
  }
} 
